import { Link } from "react-router-dom";
import { motion } from "framer-motion";

import FeatureCarousel from "../About/components/FeatureCarousel";
import { featureCardData } from "../About/data/featureCardData";
import UnlockingIntelligence from "../About/components/UnlockingIntelligence";
import { companyLogos } from "./data/companyLogos";

import { MoveRight, Play } from "lucide-react";

function Home() {
  return (
    <>
      <div className="bg-dark-bg">
        <div className="min-h-screen flex items-center justify-center px-5 md:px-0">
          <div className="container mx-auto w-full max-w-5xl text-center">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="text-5xl sm:text-5xl md:text-6xl lg:text-7xl leading-tight font-bold tracking-tight mt-5"
            >
              <span className="bg-gradient-text bg-clip-text text-transparent">
                Transform Chemical Data <br /> Into Strategic Intelligence
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
              className="mt-6 md:mt-8 text-white/90 text-base sm:text-lg md:text-xl max-w-4xl mx-auto leading-relaxed px-2"
            >
              Decades of chemical know-how is buried in old reports, notebooks,
              and scans. We turn it into searchable, structured data your team
              can actually use.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
              className="mt-8 md:mt-12 flex flex-col sm:flex-row flex-wrap gap-4 md:gap-5 justify-center"
            >
              <Link
                to="/pricingPlans"
                className="inline-flex items-center justify-center bg-gradient-to-r from-[#00A3FF] to-[#0077CC] hover:shadow-xl hover:shadow-[#00A3FF]/40 rounded-lg px-6 py-3 sm:px-8 sm:py-4 text-white text-base sm:text-lg font-semibold transition-all duration-200 hover:scale-105"
              >
                Get Started <MoveRight className="ml-2" size={20} />
              </Link>
              <Link
                to="https://www.youtube.com/watch?v=I4h8H3v0-rQ"
                target="_blank"
                className="inline-flex items-center justify-center bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg px-6 py-3 sm:px-8 sm:py-4 text-white text-base sm:text-lg font-semibold transition-all duration-200 hover:scale-105"
              >
                Watch Demo <Play className="ml-2" size={20} />
              </Link>
            </motion.div>

            {/* Analytics Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6, ease: "easeOut" }}
              className="mt-16 text-center"
            >
              <div className="text-4xl md:text-5xl font-semibold tracking-tight text-white mb-2 tabular-nums">
                1,000,000+
              </div>
              <div className="text-white/50 text-xs md:text-sm uppercase tracking-widest">
                Pages Processed
              </div>
            </motion.div>
          </div>
        </div>

        {/* Trusted By Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8, ease: "easeOut" }}
          className="w-full py-16 overflow-hidden"
        >
          <div className="text-center mb-8">
            <p className="text-white/40 text-[10px] uppercase tracking-widest font-medium">
              Trusted by leading organisations
            </p>
          </div>

          <div className="relative">
            <div className="flex animate-scroll">
              {/* First set of logos */}
              <div className="flex items-center gap-12 md:gap-16 px-8 shrink-0">
                {companyLogos.map((company) => (
                  <div
                    key={`logo-1-${company.id}`}
                    className="flex items-center justify-center w-40 h-20 md:w-48 md:h-24"
                  >
                    <img
                      src={company.logo}
                      alt={company.alt}
                      className="h-12 md:h-16 w-auto object-contain opacity-60 hover:opacity-100 transition-opacity duration-300"
                    />
                  </div>
                ))}
              </div>

              {/* Duplicate set for seamless loop */}
              <div className="flex items-center gap-12 md:gap-16 px-8 shrink-0">
                {companyLogos.map((company) => (
                  <div
                    key={`logo-2-${company.id}`}
                    className="flex items-center justify-center w-40 h-20 md:w-48 md:h-24"
                  >
                    <img
                      src={company.logo}
                      alt={company.alt}
                      className="h-12 md:h-16 w-auto object-contain opacity-60 hover:opacity-100 transition-opacity duration-300"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* hidden intelligence */}
        <motion.section
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="bg-dark-bg"
          id="why"
        >
          <div className="container mx-auto w-full max-w-5xl px-5 md:px-0">
            <div className="border-t border-white/10">
              <UnlockingIntelligence />
            </div>
          </div>
        </motion.section>

        {/* how it works  */}
        <section className="pb-16">
          <div className="container mx-auto w-full max-w-5xl px-5 md:px-0">
            <div className="border-t border-white/10 pt-16">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="mb-12 text-center"
              >
                <p className="text-xs uppercase tracking-widest text-white/40 mb-3">
                  Process
                </p>
                <h2 className="font-semibold tracking-tight text-white text-3xl md:text-4xl">
                  How we revive information's potential
                </h2>
              </motion.div>
            </div>
          </div>

          <div className="container mx-auto w-full px-5 md:px-0">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="flex justify-center items-center"
            >
              <div className="w-full max-w-6xl">
                <FeatureCarousel featureCards={featureCardData} />
              </div>
            </motion.div>
          </div>
        </section>

        <section className="pb-24">
          <div className="container mx-auto w-full max-w-5xl px-5 md:px-0">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="border-t border-white/10 pt-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6"
            >
              <div>
                <p className="text-xs uppercase tracking-widest text-white/40 mb-2">
                  Get started
                </p>
                <h3 className="font-semibold tracking-tight text-xl text-white">
                  Amplify your data. Accelerate your research.
                </h3>
                <p className="text-white/50 text-sm mt-1 max-w-md">
                  Stop sifting, start researching. Harness the full potential of
                  your chemical data, efficiently and effectively.
                </p>
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center bg-gradient-to-r from-[#00A3FF] to-[#0077CC] hover:shadow-lg hover:shadow-[#00A3FF]/25 rounded-lg px-8 py-3 text-white text-sm font-semibold transition-all duration-200 shrink-0"
              >
                Learn how we can help you
              </Link>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
}

export default Home;
